import React, { useState } from 'react';
import { SearchIcon, PlusIcon, CalendarIcon, DollarSignIcon, CheckCircleIcon, ClockIcon, XIcon, AlertTriangleIcon, FileIcon, FileTextIcon, CheckIcon } from 'lucide-react';
export function ContractPaymentClaims({
  locale
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [formData, setFormData] = useState({
    contractId: '',
    claimNumber: '',
    description: '',
    claimDate: '',
    dueDate: '',
    approvalDate: '',
    paymentDate: '',
    amount: '',
    status: 'Draft',
    documents: [],
    notes: ''
  });
  // Format currency
  const formatCurrency = value => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  // Sample payment claims data
  const paymentClaims = [{
    id: 'PCL-2023-001',
    contractId: 'CTR-2023-002',
    contractName: 'IT Support Services',
    vendor: 'TechPro Solutions',
    claimNumber: '1',
    description: 'Monthly service fee - January 2023',
    claimDate: '2023-01-25',
    dueDate: '2023-02-25',
    approvalDate: '2023-01-28',
    paymentDate: '2023-02-05',
    amount: 10000,
    status: 'Paid',
    documents: ['invoice-jan-2023.pdf'],
    notes: 'Payment processed early'
  }, {
    id: 'PCL-2023-002',
    contractId: 'CTR-2023-002',
    contractName: 'IT Support Services',
    vendor: 'TechPro Solutions',
    claimNumber: '2',
    description: 'Monthly service fee - February 2023',
    claimDate: '2023-02-25',
    dueDate: '2023-03-25',
    approvalDate: '2023-02-28',
    paymentDate: '2023-03-15',
    amount: 10000,
    status: 'Paid',
    documents: ['invoice-feb-2023.pdf'],
    notes: ''
  }, {
    id: 'PCL-2023-003',
    contractId: 'CTR-2023-002',
    contractName: 'IT Support Services',
    vendor: 'TechPro Solutions',
    claimNumber: '3',
    description: 'Monthly service fee - March 2023 + Additional services',
    claimDate: '2023-03-25',
    dueDate: '2023-04-25',
    approvalDate: '2023-03-30',
    paymentDate: '',
    amount: 12500,
    status: 'Approved',
    documents: ['invoice-mar-2023.pdf', 'additional-services.pdf'],
    notes: 'Additional emergency support services'
  }, {
    id: 'PCL-2023-004',
    contractId: 'CTR-2023-001',
    contractName: 'Office Lease Agreement',
    vendor: 'Skyline Properties Inc.',
    claimNumber: '1',
    description: 'Quarterly lease payment Q1 2023',
    claimDate: '2023-01-15',
    dueDate: '2023-01-30',
    approvalDate: '2023-01-18',
    paymentDate: '2023-01-25',
    amount: 60000,
    status: 'Paid',
    documents: ['lease-invoice-q1-2023.pdf'],
    notes: ''
  }, {
    id: 'PCL-2023-005',
    contractId: 'CTR-2023-001',
    contractName: 'Office Lease Agreement',
    vendor: 'Skyline Properties Inc.',
    claimNumber: '2',
    description: 'Quarterly lease payment Q2 2023',
    claimDate: '2023-04-15',
    dueDate: '2023-04-30',
    approvalDate: '',
    paymentDate: '',
    amount: 60000,
    status: 'Pending Approval',
    documents: ['lease-invoice-q2-2023.pdf'],
    notes: ''
  }, {
    id: 'PCL-2023-006',
    contractId: 'CTR-2023-006',
    contractName: 'Consulting Services',
    vendor: 'Business Consultants Ltd.',
    claimNumber: '1',
    description: 'Project phase 1 completion payment',
    claimDate: '2023-03-10',
    dueDate: '2023-04-10',
    approvalDate: '',
    paymentDate: '',
    amount: 35000,
    status: 'Disputed',
    documents: ['invoice-phase1.pdf', 'deliverable-issues.pdf'],
    notes: 'Deliverables did not meet requirements, under review'
  }, {
    id: 'PCL-2023-007',
    contractId: 'CTR-2023-010',
    contractName: 'Cloud Infrastructure',
    vendor: 'AWS',
    claimNumber: '1',
    description: 'Monthly cloud services - March 2023',
    claimDate: '2023-04-01',
    dueDate: '2023-04-15',
    approvalDate: '',
    paymentDate: '',
    amount: 15000,
    status: 'Draft',
    documents: ['aws-invoice-mar-2023.pdf'],
    notes: 'Awaiting usage verification'
  }];
  // Filter payment claims based on search term
  const filteredClaims = paymentClaims.filter(claim => claim.id.toLowerCase().includes(searchTerm.toLowerCase()) || claim.contractId.toLowerCase().includes(searchTerm.toLowerCase()) || claim.contractName.toLowerCase().includes(searchTerm.toLowerCase()) || claim.vendor.toLowerCase().includes(searchTerm.toLowerCase()) || claim.description.toLowerCase().includes(searchTerm.toLowerCase()));
  // Get status badge color
  const getStatusColor = status => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Approved':
        return 'bg-blue-100 text-blue-800';
      case 'Pending Approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Disputed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status icon
  const getStatusIcon = status => {
    switch (status) {
      case 'Paid':
        return CheckCircleIcon;
      case 'Approved':
        return CheckIcon;
      case 'Pending Approval':
        return ClockIcon;
      case 'Draft':
        return FileTextIcon;
      case 'Disputed':
        return AlertTriangleIcon;
      default:
        return FileTextIcon;
    }
  };
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    // In a real app, this would save the payment claim to the database
    console.log('Saving payment claim:', formData);
    // Show success message
    alert(`Payment claim saved successfully!`);
    // Reset form and view
    setShowForm(false);
    setSelectedClaim(null);
  };
  const handleAddNew = () => {
    setFormData({
      contractId: '',
      claimNumber: '',
      description: '',
      claimDate: '',
      dueDate: '',
      approvalDate: '',
      paymentDate: '',
      amount: '',
      status: 'Draft',
      documents: [],
      notes: ''
    });
    setSelectedClaim(null);
    setShowForm(true);
  };
  const handleEdit = claim => {
    setFormData({
      contractId: claim.contractId,
      claimNumber: claim.claimNumber,
      description: claim.description,
      claimDate: claim.claimDate,
      dueDate: claim.dueDate,
      approvalDate: claim.approvalDate,
      paymentDate: claim.paymentDate,
      amount: claim.amount.toString(),
      status: claim.status,
      documents: claim.documents,
      notes: claim.notes
    });
    setSelectedClaim(claim);
    setShowForm(true);
  };
  return <div data-id="element-2904">
      <div className="mb-8 flex justify-between items-center" data-id="element-2905">
        <div data-id="element-2906">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-2907">
            Contract Payment Claims
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2908">
            Manage payment claims for your contracts
          </p>
        </div>
        <button onClick={handleAddNew} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-2909">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-2910" />
          Add Payment Claim
        </button>
      </div>
      {showForm ? <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2911">
          <form onSubmit={handleSubmit} className="p-6" data-id="element-2912">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-2913">
              {/* Contract Selection */}
              <div className="sm:col-span-3" data-id="element-2914">
                <label htmlFor="contractId" className="block text-sm font-medium text-gray-700" data-id="element-2915">
                  Contract
                </label>
                <div className="mt-1" data-id="element-2916">
                  <select id="contractId" name="contractId" value={formData.contractId} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2917">
                    <option value="" data-id="element-2918">Select a contract</option>
                    <option value="CTR-2023-001" data-id="element-2919">
                      CTR-2023-001 - Office Lease Agreement
                    </option>
                    <option value="CTR-2023-002" data-id="element-2920">
                      CTR-2023-002 - IT Support Services
                    </option>
                    <option value="CTR-2023-006" data-id="element-2921">
                      CTR-2023-006 - Consulting Services
                    </option>
                    <option value="CTR-2023-008" data-id="element-2922">
                      CTR-2023-008 - Security Services
                    </option>
                    <option value="CTR-2023-010" data-id="element-2923">
                      CTR-2023-010 - Cloud Infrastructure
                    </option>
                  </select>
                </div>
              </div>
              {/* Claim Number */}
              <div className="sm:col-span-3" data-id="element-2924">
                <label htmlFor="claimNumber" className="block text-sm font-medium text-gray-700" data-id="element-2925">
                  Claim Number
                </label>
                <div className="mt-1" data-id="element-2926">
                  <input type="text" name="claimNumber" id="claimNumber" value={formData.claimNumber} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2927" />
                </div>
              </div>
              {/* Description */}
              <div className="sm:col-span-6" data-id="element-2928">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-2929">
                  Description
                </label>
                <div className="mt-1" data-id="element-2930">
                  <input type="text" name="description" id="description" value={formData.description} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2931" />
                </div>
              </div>
              {/* Claim Date */}
              <div className="sm:col-span-3" data-id="element-2932">
                <label htmlFor="claimDate" className="block text-sm font-medium text-gray-700" data-id="element-2933">
                  Claim Date
                </label>
                <div className="mt-1" data-id="element-2934">
                  <input type="date" name="claimDate" id="claimDate" value={formData.claimDate} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2935" />
                </div>
              </div>
              {/* Due Date */}
              <div className="sm:col-span-3" data-id="element-2936">
                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700" data-id="element-2937">
                  Due Date
                </label>
                <div className="mt-1" data-id="element-2938">
                  <input type="date" name="dueDate" id="dueDate" value={formData.dueDate} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2939" />
                </div>
              </div>
              {/* Amount */}
              <div className="sm:col-span-3" data-id="element-2940">
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700" data-id="element-2941">
                  Amount
                </label>
                <div className="mt-1 relative rounded-md shadow-sm" data-id="element-2942">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2943">
                    <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-2944" />
                  </div>
                  <input type="number" name="amount" id="amount" value={formData.amount} onChange={handleChange} required className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="0.00" data-id="element-2945" />
                </div>
              </div>
              {/* Status */}
              <div className="sm:col-span-3" data-id="element-2946">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-2947">
                  Status
                </label>
                <div className="mt-1" data-id="element-2948">
                  <select id="status" name="status" value={formData.status} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2949">
                    <option value="Draft" data-id="element-2950">Draft</option>
                    <option value="Pending Approval" data-id="element-2951">Pending Approval</option>
                    <option value="Approved" data-id="element-2952">Approved</option>
                    <option value="Paid" data-id="element-2953">Paid</option>
                    <option value="Disputed" data-id="element-2954">Disputed</option>
                  </select>
                </div>
              </div>
              {/* Approval Date - only shown for certain statuses */}
              {(formData.status === 'Approved' || formData.status === 'Paid') && <div className="sm:col-span-3" data-id="element-2955">
                  <label htmlFor="approvalDate" className="block text-sm font-medium text-gray-700" data-id="element-2956">
                    Approval Date
                  </label>
                  <div className="mt-1" data-id="element-2957">
                    <input type="date" name="approvalDate" id="approvalDate" value={formData.approvalDate} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2958" />
                  </div>
                </div>}
              {/* Payment Date - only shown for Paid status */}
              {formData.status === 'Paid' && <div className="sm:col-span-3" data-id="element-2959">
                  <label htmlFor="paymentDate" className="block text-sm font-medium text-gray-700" data-id="element-2960">
                    Payment Date
                  </label>
                  <div className="mt-1" data-id="element-2961">
                    <input type="date" name="paymentDate" id="paymentDate" value={formData.paymentDate} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2962" />
                  </div>
                </div>}
              {/* Document Upload */}
              <div className="sm:col-span-6" data-id="element-2963">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2964">
                  Documents
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-2965">
                  <div className="space-y-1 text-center" data-id="element-2966">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true" data-id="element-2967">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" data-id="element-2968" />
                    </svg>
                    <div className="flex text-sm text-gray-600" data-id="element-2969">
                      <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500" data-id="element-2970">
                        <span data-id="element-2971">Upload files</span>
                        <input id="file-upload" name="file-upload" type="file" className="sr-only" multiple data-id="element-2972" />
                      </label>
                      <p className="pl-1" data-id="element-2973">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-2974">
                      PDF, DOC, DOCX, XLS, XLSX up to 10MB
                    </p>
                  </div>
                </div>
              </div>
              {/* Attached Documents List */}
              {formData.documents.length > 0 && <div className="sm:col-span-6" data-id="element-2975">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2976">
                    Attached Documents
                  </h4>
                  <ul className="border border-gray-200 rounded-md divide-y divide-gray-200" data-id="element-2977">
                    {formData.documents.map((doc, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-2978">
                        <div className="w-0 flex-1 flex items-center" data-id="element-2979">
                          <FileIcon className="flex-shrink-0 h-5 w-5 text-gray-400" data-id="element-2980" />
                          <span className="ml-2 flex-1 w-0 truncate" data-id="element-2981">
                            {doc}
                          </span>
                        </div>
                        <div className="ml-4 flex-shrink-0" data-id="element-2982">
                          <button type="button" className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-2983">
                            Remove
                          </button>
                        </div>
                      </li>)}
                  </ul>
                </div>}
              {/* Notes */}
              <div className="sm:col-span-6" data-id="element-2984">
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700" data-id="element-2985">
                  Notes
                </label>
                <div className="mt-1" data-id="element-2986">
                  <textarea id="notes" name="notes" rows={3} value={formData.notes} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2987" />
                </div>
              </div>
            </div>
            <div className="pt-5 mt-6 border-t border-gray-200" data-id="element-2988">
              <div className="flex justify-end" data-id="element-2989">
                <button type="button" onClick={() => setShowForm(false)} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none" data-id="element-2990">
                  Cancel
                </button>
                <button type="submit" className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-2991">
                  {selectedClaim ? 'Update Payment Claim' : 'Create Payment Claim'}
                </button>
              </div>
            </div>
          </form>
        </div> : <>
          {/* Search */}
          <div className="mb-6" data-id="element-2992">
            <div className="max-w-md" data-id="element-2993">
              <div className="relative rounded-md shadow-sm" data-id="element-2994">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2995">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2996" />
                </div>
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search payment claims..." data-id="element-2997" />
              </div>
            </div>
          </div>
          {/* Payment Claims Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2998">
            <div className="overflow-x-auto" data-id="element-2999">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-3000">
                <thead className="bg-gray-50" data-id="element-3001">
                  <tr data-id="element-3002">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3003">
                      Claim
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3004">
                      Contract
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3005">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3006">
                      Dates
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3007">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3008">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3009">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-3010">
                  {filteredClaims.length === 0 ? <tr data-id="element-3011">
                      <td colSpan={7} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-3012">
                        No payment claims found matching your criteria.
                      </td>
                    </tr> : filteredClaims.map(claim => {
                const StatusIcon = getStatusIcon(claim.status);
                return <tr key={claim.id} className="hover:bg-gray-50" data-id="element-3013">
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-3014">
                            <div className="flex items-center" data-id="element-3015">
                              <div data-id="element-3016">
                                <div className="text-sm font-medium text-indigo-600" data-id="element-3017">
                                  {claim.id}
                                </div>
                                <div className="text-sm text-gray-500" data-id="element-3018">
                                  Claim #{claim.claimNumber}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-3019">
                            <div className="text-sm text-gray-900" data-id="element-3020">
                              {claim.contractId}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-3021">
                              {claim.contractName}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-3022">
                              {claim.vendor}
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-3023">
                            <div className="text-sm text-gray-900 max-w-xs truncate" data-id="element-3024">
                              {claim.description}
                            </div>
                            {claim.notes && <div className="text-xs text-gray-500 max-w-xs truncate" data-id="element-3025">
                                Note: {claim.notes}
                              </div>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3026">
                            <div className="flex items-center" data-id="element-3027">
                              <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-3028" />
                              <span data-id="element-3029">Claim: {claim.claimDate}</span>
                            </div>
                            <div className="flex items-center mt-1" data-id="element-3030">
                              <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-3031" />
                              <span data-id="element-3032">Due: {claim.dueDate}</span>
                            </div>
                            {claim.paymentDate && <div className="flex items-center mt-1" data-id="element-3033">
                                <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-3034" />
                                <span data-id="element-3035">Paid: {claim.paymentDate}</span>
                              </div>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3036">
                            <div className="flex items-center" data-id="element-3037">
                              <DollarSignIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-3038" />
                              {formatCurrency(claim.amount)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-3039">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(claim.status)}`} data-id="element-3040">
                              <StatusIcon className="h-3 w-3 mr-1" data-id="element-3041" />
                              {claim.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3042">
                            <div className="flex justify-end space-x-2" data-id="element-3043">
                              <button onClick={() => handleEdit(claim)} className="text-indigo-600 hover:text-indigo-900" data-id="element-3044">
                                Edit
                              </button>
                              {claim.documents.length > 0 && <button className="text-blue-600 hover:text-blue-900" title="View Documents" data-id="element-3045">
                                  <FileIcon className="h-4 w-4" data-id="element-3046" />
                                </button>}
                            </div>
                          </td>
                        </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>
        </>}
    </div>;
}